/**
 * Cognito Services Module
 * Handles email verification using AWS Cognito
 */

import {
  CognitoIdentityProviderClient,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  InitiateAuthCommand
} from '@aws-sdk/client-cognito-identity-provider';
import configServices from './configServices';

// Create Cognito client
const getCognitoClient = () => {
  const config = configServices.getCognitoConfig();
  return new CognitoIdentityProviderClient({ region: config.region });
};

// Get client ID from config
const getClientId = () => {
  return configServices.getCognitoConfig().clientId;
};

/**
 * Generate a random password for the user
 * Since we only care about email verification, the password is just a placeholder
 */
const generateTempPassword = () => {
  return `Temp${Date.now()}!${Math.random().toString(36).substring(2, 10)}`;
};

/**
 * Check if an email is already verified in Cognito
 * We do this by trying to initiate auth - if the user exists and is confirmed, they're verified
 */
export const checkEmailVerified = async (email) => {
  // Check localStorage for previously verified emails
  const verifiedEmails = JSON.parse(localStorage.getItem('verifiedEmails') || '{}');
  if (verifiedEmails[email.toLowerCase()]) {
    return { verified: true, email: email.toLowerCase() };
  }
  return { verified: false, email: email.toLowerCase() };
};

/**
 * Sign up a new user with email - this triggers sending verification code
 */
export const sendVerificationCode = async (email) => {
  const client = getCognitoClient();
  const clientId = getClientId();
  const normalizedEmail = email.toLowerCase().trim();
  
  // Store the temp password so we can use it later if needed
  const tempPassword = generateTempPassword();
  sessionStorage.setItem(`tempPwd_${normalizedEmail}`, tempPassword);
  
  try {
    const command = new SignUpCommand({
      ClientId: clientId,
      Username: normalizedEmail,
      Password: tempPassword,
      UserAttributes: [
        {
          Name: 'email',
          Value: normalizedEmail
        }
      ]
    });
    
    await client.send(command);
    return { success: true, message: 'Verification code sent' };
  } catch (error) {
    console.error('Cognito SignUp error:', error);
    
    // If user already exists but not confirmed, resend the code
    if (error.name === 'UsernameExistsException') {
      try {
        const resendCommand = new ResendConfirmationCodeCommand({
          ClientId: clientId,
          Username: normalizedEmail
        });
        await client.send(resendCommand);
        return { success: true, message: 'Verification code resent' };
      } catch (resendError) {
        // User might already be confirmed
        if (resendError.name === 'InvalidParameterException' && 
            resendError.message?.includes('confirmed')) {
          // Mark as verified
          const verifiedEmails = JSON.parse(localStorage.getItem('verifiedEmails') || '{}');
          verifiedEmails[normalizedEmail] = Date.now();
          localStorage.setItem('verifiedEmails', JSON.stringify(verifiedEmails));
          return { success: true, alreadyVerified: true, message: 'Email already verified' };
        }
        throw resendError;
      }
    }
    
    throw error;
  }
};

/**
 * Verify the code submitted by the user
 */
export const verifyEmailCode = async (email, code) => {
  const client = getCognitoClient();
  const clientId = getClientId();
  const normalizedEmail = email.toLowerCase().trim();
  
  try {
    const command = new ConfirmSignUpCommand({
      ClientId: clientId,
      Username: normalizedEmail,
      ConfirmationCode: code
    });
    
    await client.send(command);
    
    // Store verified email in localStorage
    const verifiedEmails = JSON.parse(localStorage.getItem('verifiedEmails') || '{}');
    verifiedEmails[normalizedEmail] = Date.now();
    localStorage.setItem('verifiedEmails', JSON.stringify(verifiedEmails));
    
    return { success: true, verified: true, message: 'Email verified successfully' };
  } catch (error) {
    console.error('Cognito ConfirmSignUp error:', error);
    
    if (error.name === 'CodeMismatchException') {
      return { success: false, verified: false, message: 'Invalid verification code' };
    }
    if (error.name === 'ExpiredCodeException') {
      return { success: false, verified: false, message: 'Verification code has expired. Please request a new one.' };
    }
    if (error.name === 'NotAuthorizedException' && error.message?.includes('CONFIRMED')) {
      // User is already confirmed
      const verifiedEmails = JSON.parse(localStorage.getItem('verifiedEmails') || '{}');
      verifiedEmails[normalizedEmail] = Date.now();
      localStorage.setItem('verifiedEmails', JSON.stringify(verifiedEmails));
      return { success: true, verified: true, message: 'Email already verified' };
    }
    
    return { success: false, verified: false, message: error.message || 'Verification failed' };
  }
};

/**
 * Resend verification code
 */
export const resendVerificationCode = async (email) => {
  const client = getCognitoClient();
  const clientId = getClientId();
  const normalizedEmail = email.toLowerCase().trim();
  
  try {
    const command = new ResendConfirmationCodeCommand({
      ClientId: clientId,
      Username: normalizedEmail
    });
    
    await client.send(command);
    return { success: true, message: 'Verification code resent' };
  } catch (error) {
    console.error('Cognito ResendCode error:', error);
    throw error;
  }
};

export default {
  checkEmailVerified,
  sendVerificationCode,
  verifyEmailCode,
  resendVerificationCode
};
